/**
 * Brand tokens — JS mirror of the CSS custom properties in `src/index.css` `:root`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE CSS IS THE SOURCE OF TRUTH. This file exists only for the three contexts
 * where a CSS variable genuinely cannot be used:
 *
 *   1. AntD `ConfigProvider` theme tokens — AntD *parses* the colour to derive
 *      its own palette (hover/active/bg/border steps). Handing it `var(--brand)`
 *      yields an unparseable string and the derived colours collapse.
 *   2. Recharts / SVG **presentation attributes** (`stroke={x}`, `fill={x}` on
 *      chart primitives). Browsers do not resolve `var()` inside SVG attributes
 *      — only inside CSS declarations. (An inline `style={{ stroke: 'var(--brand)' }}`
 *      IS a CSS declaration and works fine — prefer that where possible.)
 *   3. `<meta name="theme-color">` in `index.html` (static HTML, no cascade).
 *
 * Everywhere else — CSS files, `<style>` blocks, inline `style={{}}` objects —
 * use `var(--brand)` / `var(--brand-50)` / … directly. Do NOT import from here.
 *
 * To change the brand colour: edit `:root` in `src/index.css`, then mirror the
 * handful of values below. Keep the two in sync — they are checked by eye, not
 * by the compiler.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/** `--brand` — Swiftlane blue. The one brand accent. */
export const BRAND = '#4119F3';
/** `--brand-600` — brighter step; the gradient's middle stop. */
export const BRAND_600 = '#4A3CFF';
/** `--brand-700` — dark step. Readable text on `--brand-50`/`--brand-100`. */
export const BRAND_700 = '#382EC4';
/** `--brand-400` — light step used for input hover borders. */
export const BRAND_400 = '#7685E7';
/** `--brand-300` / `--brand-200` — pale steps, chart ramps and soft borders. */
export const BRAND_300 = '#A9B6F5';
export const BRAND_200 = '#C3CDF9';
/** `--brand-border` — the soft border on brand-tinted surfaces. */
export const BRAND_BORDER = '#D2DBFD';
/** `--brand-100` / `--brand-50` / `--brand-tint` — tint fills, lightest last. */
export const BRAND_100 = '#DFE5FF';
export const BRAND_50 = '#EFF2FF';
export const BRAND_TINT = '#F8FAFF';
/** `--grad-end` — the gradient's final stop (the bluest point). */
export const GRAD_END = '#3D63FF';

/**
 * Ordered dark→light ramp for categorical chart series (donut slices, stacked
 * bars). Mirrors `--brand-700 → --brand → --brand-600 → --grad-end → --brand-300
 * → --brand-200`. Chart primitives take SVG attributes, so these must be literals.
 */
export const BRAND_CHART_RAMP = [
  BRAND_700,
  BRAND,
  BRAND_600,
  GRAD_END,
  BRAND_300,
  BRAND_200,
] as const;
